import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { format } from "date-fns";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { useToast } from "@/components/ui/use-toast";
import {
  Loader2,
  ChevronDown,
  ChevronUp,
  UserCircle,
  CalendarClock,
  Calendar as CalendarIcon,
  ChevronLeft,
  MapPin,
} from "lucide-react";
import { Shield } from "lucide-react";
import { useAuthStore } from "@/stores/authStore";
import { CONSOLE_PERMISSIONS } from "@/constants/permissions";
import { cn } from "@/lib/utils";
import { useNavigate } from "react-router-dom";
import { appointmentsAPI } from "@/lib/appointments-api";
import { getApiErrorMessage, getApiErrorStatus } from "@/lib/api";
import type { ServiceStaff } from "@/types/appointments";
import { useClinics } from "@/hooks/useClinics";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";

// Sentinel used in the Clinic <Select> to represent "all clinics" (clinic: null).
// Radix Select does not allow an empty-string item value, so we map this
// sentinel to `null` on submit and back to itself when the field is empty.
const ALL_CLINICS_VALUE = "__all_clinics__";

const BLOCK_MODE_VALUES = ["all", "teleconsult", "clinic"] as const;
type BlockMode = (typeof BLOCK_MODE_VALUES)[number];

// Form validation schema
const blockedTimeSchema = z
  .object({
    staffMember: z.string().min(1, "Staff member is required"),
    title: z
      .string()
      .min(1, "Title is required")
      .max(250, "Title must be 250 characters or less"),
    startDateTime: z.date({
      required_error: "Start date and time is required",
    }),
    endDateTime: z.date({
      required_error: "End date and time is required",
    }),
    note: z.string().optional(),
    blockMode: z.enum(BLOCK_MODE_VALUES).default("all"),
    clinicId: z.string().nullable().default(null),
  })
  .refine((data) => data.endDateTime > data.startDateTime, {
    message: "End time must be after start time",
    path: ["endDateTime"],
  })
  .refine((data) => data.blockMode === "clinic" || !data.clinicId, {
    message: "Clinic can only be set when Block Mode is \"Clinic Only\"",
    path: ["clinicId"],
  });

type BlockedTimeFormValues = z.infer<typeof blockedTimeSchema>;

export const AddBlockedTimeForm = () => {
  const { toast } = useToast();
  const { hasPermission } = useAuthStore();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showNoteSection, setShowNoteSection] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [errorOpen, setErrorOpen] = useState(false);
  const [submitErrorMessage, setSubmitErrorMessage] = useState("");
  const [isSubmitPermissionError, setIsSubmitPermissionError] = useState(false);
  const [staffMembers, setStaffMembers] = useState<ServiceStaff[]>([]);
  const [isLoadingStaff, setIsLoadingStaff] = useState(false);
  const { data: clinics = [] } = useClinics();
  const hasClinics = clinics.length > 0;

  const form = useForm<BlockedTimeFormValues>({
    resolver: zodResolver(blockedTimeSchema),
    defaultValues: {
      staffMember: "",
      title: "",
      startDateTime: new Date(),
      endDateTime: new Date(Date.now() + 60 * 60 * 1000), // 1 hour later
      note: "",
      blockMode: "all",
      clinicId: null,
    },
  });

  const blockMode = form.watch("blockMode");

  // block_mode is only meaningful when clinics exist. If the feature flag is
  // off (no clinics), keep it forced to "all" so the payload always matches
  // what the form shows.
  useEffect(() => {
    if (!hasClinics && blockMode !== "all") {
      form.setValue("blockMode", "all");
      form.setValue("clinicId", null);
    }
  }, [hasClinics, blockMode, form]);

  // Clear the clinic selection whenever block mode moves away from "clinic".
  useEffect(() => {
    if (blockMode !== "clinic") {
      form.setValue("clinicId", null);
    }
  }, [blockMode, form]);

  // Fetch staff members on mount
  useEffect(() => {
    const fetchStaff = async () => {
      setIsLoadingStaff(true);
      try {
        const response = await appointmentsAPI.getServiceStaff();
        setStaffMembers(response);
      } catch (error) {
        console.error("Error fetching staff:", error);
        toast({
          title: "Error",
          description:
            "Failed to load staff members. Please check your connection.",
          variant: "destructive",
        });
      } finally {
        setIsLoadingStaff(false);
      }
    };

    fetchStaff();
  }, [toast]);

  if (!hasPermission(CONSOLE_PERMISSIONS.MANAGE_APPOINTMENTS)) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-destructive" />
            Access Denied
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <Shield className="h-16 w-16 text-destructive mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-foreground mb-2">
              Permission Required
            </h3>
            <p className="text-muted-foreground mb-4">
              You need appointment-management permission to access this feature.
            </p>
            <p className="text-sm text-muted-foreground">
              Please contact your administrator if you believe this is an error.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const handleSubmit = (_values: BlockedTimeFormValues) => {
    setReviewOpen(true);
  };

  const handleConfirmSubmit = async () => {
    setIsSubmitting(true);
    try {
      const values = form.getValues();

      // Format dates to ISO 8601 with timezone
      const formatDateTime = (date: Date) => {
        return format(date, "yyyy-MM-dd'T'HH:mm:ssxxx");
      };

      const payload = {
        staff_id: values.staffMember,
        start_time: formatDateTime(values.startDateTime),
        end_time: formatDateTime(values.endDateTime),
        title: values.title || undefined,
        note: values.note || undefined,
        block_mode: values.blockMode,
        clinic: values.blockMode === "clinic" ? values.clinicId : null,
      };

      await appointmentsAPI.createTimeBlock(values.staffMember, payload);

      setReviewOpen(false);
      setSuccessOpen(true);
    } catch (error: unknown) {
      const isPermissionError = getApiErrorStatus(error) === 403;
      console.error("Error creating blocked time:", error);
      setReviewOpen(false);
      setIsSubmitPermissionError(isPermissionError);
      setSubmitErrorMessage(
        isPermissionError
          ? "Your permissions no longer allow appointment time blocking."
          : getApiErrorMessage(error, "Failed to block the selected time.")
      );
      setErrorOpen(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSuccessOk = () => {
    setSuccessOpen(false);

    // Reset form to initial state with fresh dates
    const now = new Date();
    const oneHourLater = new Date(now.getTime() + 60 * 60 * 1000);

    form.reset({
      staffMember: "",
      title: "",
      startDateTime: now,
      endDateTime: oneHourLater,
      note: "",
      blockMode: "all",
      clinicId: null,
    });

    // Reset UI state
    setShowNoteSection(false);

    toast({
      title: "Success",
      description: "Time blocked successfully!",
    });
  };

  return (
    <>
      <Card className="h-full flex flex-col">
        <CardHeader className="shrink-0">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/settings/appointments/calendar")}
              className="h-8 w-8"
            >
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <CardTitle>Block Off Time</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="flex-1 overflow-y-auto">
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(handleSubmit)}
              className="space-y-6 max-w-5xl mx-auto"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Staff Member Selection */}
                <FormField
                  control={form.control}
                  name="staffMember"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center gap-2">
                        <UserCircle className="h-4 w-4" />
                        Staff
                      </FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                        disabled={isLoadingStaff}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue
                              placeholder={
                                isLoadingStaff
                                  ? "Loading staff..."
                                  : "Select Member"
                              }
                            >
                              {field.value &&
                                staffMembers.find(
                                  (s) => s.doctor__id === field.value
                                ) && (
                                  <div className="flex flex-col items-start">
                                    <span className="font-medium">
                                      {
                                        staffMembers.find(
                                          (s) => s.doctor__id === field.value
                                        )?.name
                                      }
                                    </span>
                                    <span className="text-xs text-muted-foreground">
                                      {
                                        staffMembers.find(
                                          (s) => s.doctor__id === field.value
                                        )?.category
                                      }
                                    </span>
                                  </div>
                                )}
                            </SelectValue>
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {staffMembers.map((staff) => (
                            <SelectItem
                              key={staff.doctor__id}
                              value={staff.doctor__id}
                            >
                              <div className="flex flex-col">
                                <span className="font-medium">
                                  {staff.name}
                                </span>
                                <span className="text-xs text-muted-foreground">
                                  {staff.category}
                                </span>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Title */}
                <FormField
                  control={form.control}
                  name="title"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center gap-2">
                        <CalendarClock className="h-4 w-4" />
                        Title
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Give your blocked time a title (Max 25 words)"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Block Mode + Clinic — only shown when clinics exist (feature flag on) */}
              {hasClinics && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <FormField
                    control={form.control}
                    name="blockMode"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="flex items-center gap-2">
                          <MapPin className="h-4 w-4" />
                          Block Mode
                        </FormLabel>
                        <Select
                          onValueChange={(value) =>
                            field.onChange(value as BlockMode)
                          }
                          value={field.value}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select block mode" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="all">All Modes</SelectItem>
                            <SelectItem value="teleconsult">
                              Teleconsult Only
                            </SelectItem>
                            <SelectItem value="clinic">
                              Clinic Only
                            </SelectItem>
                          </SelectContent>
                        </Select>
                        <FormDescription>
                          Which booking modes this block applies to.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {blockMode === "clinic" && (
                    <FormField
                      control={form.control}
                      name="clinicId"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="flex items-center gap-2">
                            <MapPin className="h-4 w-4" />
                            Specific clinic (optional)
                          </FormLabel>
                          <Select
                            onValueChange={(value) =>
                              field.onChange(
                                value === ALL_CLINICS_VALUE ? null : value
                              )
                            }
                            value={field.value ?? ALL_CLINICS_VALUE}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="All clinics" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value={ALL_CLINICS_VALUE}>
                                All clinics
                              </SelectItem>
                              {clinics.map((clinic) => (
                                <SelectItem key={clinic.id} value={clinic.id}>
                                  {clinic.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormDescription>
                            Leave as &quot;All clinics&quot; to block clinic
                            slots at every location, or pick one clinic to
                            restrict the block to it.
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Start Time */}
                <FormField
                  control={form.control}
                  name="startDateTime"
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel className="flex items-center gap-2">
                        <span className="text-lg">🕒</span>
                        Start Time
                      </FormLabel>
                      <Popover>
                        <PopoverTrigger asChild>
                          <FormControl>
                            <Button
                              variant="outline"
                              className={cn(
                                "w-full pl-3 text-left font-normal",
                                !field.value && "text-muted-foreground"
                              )}
                            >
                              {field.value ? (
                                format(field.value, "PPP 'at' p")
                              ) : (
                                <span>Pick start date and time</span>
                              )}
                              <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                            </Button>
                          </FormControl>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={field.value}
                            onSelect={(date) => {
                              if (date) {
                                const currentTime = field.value || new Date();
                                date.setHours(currentTime.getHours());
                                date.setMinutes(currentTime.getMinutes());
                                field.onChange(date);
                              }
                            }}
                            initialFocus
                          />
                          <div className="p-3 border-t">
                            <label className="text-sm font-medium mb-2 block">
                              Time
                            </label>
                            <Input
                              type="time"
                              value={
                                field.value ? format(field.value, "HH:mm") : ""
                              }
                              onChange={(e) => {
                                const [hours, minutes] =
                                  e.target.value.split(":");
                                const newDate = field.value
                                  ? new Date(field.value)
                                  : new Date();
                                newDate.setHours(parseInt(hours, 10));
                                newDate.setMinutes(parseInt(minutes, 10));
                                field.onChange(newDate);
                              }}
                            />
                          </div>
                        </PopoverContent>
                      </Popover>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* End Time */}
                <FormField
                  control={form.control}
                  name="endDateTime"
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel className="flex items-center gap-2">
                        <span className="text-lg">🕒</span>
                        End Time
                      </FormLabel>
                      <Popover>
                        <PopoverTrigger asChild>
                          <FormControl>
                            <Button
                              variant="outline"
                              className={cn(
                                "w-full pl-3 text-left font-normal",
                                !field.value && "text-muted-foreground"
                              )}
                            >
                              {field.value ? (
                                format(field.value, "PPP 'at' p")
                              ) : (
                                <span>Pick end date and time</span>
                              )}
                              <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                            </Button>
                          </FormControl>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={field.value}
                            onSelect={(date) => {
                              if (date) {
                                const currentTime = field.value || new Date();
                                date.setHours(currentTime.getHours());
                                date.setMinutes(currentTime.getMinutes());
                                field.onChange(date);
                              }
                            }}
                            initialFocus
                          />
                          <div className="p-3 border-t">
                            <label className="text-sm font-medium mb-2 block">
                              Time
                            </label>
                            <Input
                              type="time"
                              value={
                                field.value ? format(field.value, "HH:mm") : ""
                              }
                              onChange={(e) => {
                                const [hours, minutes] =
                                  e.target.value.split(":");
                                const newDate = field.value
                                  ? new Date(field.value)
                                  : new Date();
                                newDate.setHours(parseInt(hours, 10));
                                newDate.setMinutes(parseInt(minutes, 10));
                                field.onChange(newDate);
                              }}
                            />
                          </div>
                        </PopoverContent>
                      </Popover>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Add note to self - Expandable Section */}
              <div className="border rounded-lg">
                <button
                  type="button"
                  onClick={() => setShowNoteSection(!showNoteSection)}
                  className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium hover:bg-muted/30 rounded-lg transition-colors"
                >
                  <span>Add note to self</span>
                  {showNoteSection ? (
                    <ChevronUp className="h-4 w-4" />
                  ) : (
                    <ChevronDown className="h-4 w-4" />
                  )}
                </button>

                {showNoteSection && (
                  <div className="px-4 pb-4">
                    <FormField
                      control={form.control}
                      name="note"
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <Textarea
                              placeholder="Add any notes for yourself..."
                              className="min-h-[100px] resize-none"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                )}
              </div>

              {/* Submit button */}
              <div className="pt-2 flex justify-end gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => form.reset()}
                  className="text-destructive hover:text-destructive hover:bg-destructive/10"
                >
                  Back
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-primary hover:bg-primary/90"
                >
                  {isSubmitting && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  Block
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>

      {/* Confirmation Dialog */}
      <AlertDialog open={reviewOpen} onOpenChange={setReviewOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Blocked Time</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-foreground">
                <div>
                  <span className="font-semibold">
                    {form.getValues("title")}
                  </span>
                </div>
                <div className="text-sm space-y-1">
                  <div>
                    <span className="text-muted-foreground">Staff: </span>
                    <span className="font-medium">
                      {
                        staffMembers.find(
                          (s) => s.doctor__id === form.getValues("staffMember")
                        )?.name
                      }
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Start: </span>
                    <span className="font-medium">
                      {form.getValues("startDateTime") &&
                        format(form.getValues("startDateTime"), "PPP 'at' p")}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">End: </span>
                    <span className="font-medium">
                      {form.getValues("endDateTime") &&
                        format(form.getValues("endDateTime"), "PPP 'at' p")}
                    </span>
                  </div>
                  {hasClinics && (
                    <div>
                      <span className="text-muted-foreground">
                        Block Mode:{" "}
                      </span>
                      <span className="font-medium">
                        {
                          {
                            all: "All Modes",
                            teleconsult: "Teleconsult Only",
                            clinic: "Clinic Only",
                          }[form.getValues("blockMode")]
                        }
                      </span>
                    </div>
                  )}
                  {form.getValues("blockMode") === "clinic" && (
                    <div>
                      <span className="text-muted-foreground">Clinic: </span>
                      <span className="font-medium">
                        {form.getValues("clinicId")
                          ? clinics.find(
                              (c) => c.id === form.getValues("clinicId")
                            )?.name
                          : "All clinics"}
                      </span>
                    </div>
                  )}
                  {form.getValues("note") && (
                    <div>
                      <span className="text-muted-foreground">Note: </span>
                      <span className="font-medium">
                        {form.getValues("note")}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => setReviewOpen(false)}
              disabled={isSubmitting}
            >
              Edit Details
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmSubmit}
              disabled={isSubmitting}
              className="bg-primary hover:bg-primary/90"
            >
              {isSubmitting && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Confirm & Block
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Success Dialog */}
      <AlertDialog open={successOpen} onOpenChange={setSuccessOpen}>
        <AlertDialogContent className="bg-success/15 border-success/30">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-success">
              Success
            </AlertDialogTitle>
            <AlertDialogDescription className="text-success">
              Time blocked successfully!
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction
              onClick={handleSuccessOk}
              className="bg-success text-success-foreground hover:bg-success/90"
            >
              OK
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Error Dialog */}
      <AlertDialog open={errorOpen} onOpenChange={setErrorOpen}>
        <AlertDialogContent className="bg-destructive/15 border-destructive/30">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-destructive">
              {isSubmitPermissionError ? "Access Denied" : "Error"}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-destructive">
              {submitErrorMessage}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction
              onClick={() => setErrorOpen(false)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Retry
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default AddBlockedTimeForm;
